import dayjs, { Dayjs } from 'dayjs';
import { string, z } from 'zod';
import { canvasImageOrUrlSchema } from '../shared/canvasImage.schema';
import {
  AssignationMode,
  BackendCompatibleLocales,
  BillingMode,
  DisplayMode,
  PositionsDistributionMode,
  InvitationApprovalMode,
  InvitationMode,
  WLAccessModeEnum,
} from '../../enums';

export const CreateExperienceGeneralFormValidatorSchema = z
  .object({
    experienceName: z.record(z.string().min(1), z.string()),
    name: z.string().min(1),
    description: z.record(z.string().min(1), z.string()),
    billingMode: z.enum(BillingMode),
    originalPrice: z.union([z.string(), z.number()]),
    price: z.string(),
    showOriginalValue: z.boolean().optional(),
    accessMode: z.enum(WLAccessModeEnum),
    /**
     * Display-only fields. They have to be declared so the resolver's input
     * type covers the step's form type, but the `.transform` at the end of this
     * schema drops them again, so they never reach the request body — a raw
     * HTMLCanvasElement would otherwise serialise as `{}` over the stored URL.
     */
    waitingListImageUrl: canvasImageOrUrlSchema,
    totalSeatPrice: z.number().optional(),
    billingVariables: z.object({
      distributionRate: z.string(),
      maxDistributionFee: z.string(),
      minDistributionFee: z.string(),
      vatRate: z.string(),
    }),
    prepaidFee: z.boolean().optional(),
  })
  .superRefine((schema, ctx) => {
    const { billingMode, price } = schema;

    if (billingMode === BillingMode.FAIR_PRICE && Number(price) <= 0) {
      ctx.addIssue({
        message: 'general_label_required',
        code: 'custom',
        path: ['price'],
      });
    }
  })
  .transform(
    ({
      waitingListImageUrl: _waitingListImageUrl,
      totalSeatPrice: _totalSeatPrice,
      ...submittable
    }) => submittable
  );

export type ExperienceGeneralFormValidator = z.infer<
  typeof CreateExperienceGeneralFormValidatorSchema
>;

export const CreateExperienceAllocationFormValidatorSchema = z
  .object({
    displayMode: z.enum(DisplayMode),
    directSales: z.boolean(),
    parking: z.boolean(),
    // The step seeds and writes the enum, never a bare string.
    positionsDistributionMode: z.enum(PositionsDistributionMode),
    voucherExpirationTimeOutInMs: z.number().nullable().optional(),
    rsvpReminderTimeOutInMs: z.number().nullable().optional(),
    invitationMode: z.enum(InvitationMode).optional(),
    termsAndConditionFileURL: string().optional(),
    joinConfirmationText: z
      .partialRecord(z.enum(BackendCompatibleLocales), z.string())
      .optional(),
    rsvpMailMessage: z
      .partialRecord(z.enum(BackendCompatibleLocales), z.string())
      .optional(),
    /**
     * `InvitationParameters` is a Partial on the entity, so a waiting list can
     * come back missing any of these. Defaults mirror the allocation step's
     * own fallback object, keeping form state partial and the output complete.
     */
    invitationParameters: z
      .object({
        approvalMode: z
          .enum(InvitationApprovalMode)
          .default(InvitationApprovalMode.DEFAULT),
        approvalRules: z
          .array(
            z.object({
              description: z.string(),
              expression: z.string(),
              name: z.string(),
            })
          )
          .optional(),
        minNumberOfInvitations: z.number().default(1),
        maxNumberOfInvitations: z.number().default(10),
        assignationMode: z
          .enum(AssignationMode)
          .default(AssignationMode.HOST_RSVP),
        invitationMode: z.enum(InvitationMode).optional(),
        specialGuestEnabled: z.boolean().default(false),
      })
      .refine(
        (data) => {
          return data.minNumberOfInvitations <= data.maxNumberOfInvitations;
        },
        {
          message: 'wl_settings_max_tickets_per_host_validation', // wl_settings_max_tickets_per_host_validation
          path: ['maxNumberOfInvitations'], // path of error
        }
      )
      .nullable(),
    minNumberOfSeatsPerPosition: z.number().optional(),
    maxNumberOfSeatsPerPosition: z.number().optional(),
    maxNumberOfParkingTicketsPerPosition: z.number().int().min(0).optional(),
    participationEndDate: z
      .instanceof(dayjs as unknown as typeof Dayjs)
      .nullable()
      .optional(),
    fixedNumberOfSeatsPerPosition: z.number().optional(),
  })
  .superRefine((schema, ctx) => {
    const {
      positionsDistributionMode,
      participationEndDate,
      minNumberOfSeatsPerPosition,
      maxNumberOfSeatsPerPosition,
    } = schema;

    const isRandomDistributionMode =
      positionsDistributionMode === PositionsDistributionMode.RANDOM ||
      positionsDistributionMode === PositionsDistributionMode.WEIGHTED_RANDOM;

    // Random-based modes need a participation end date; every mode now uses the same
    // [min, max] seat range (random derives fixedNumberOfSeatsPerPosition = max at submit).
    if (isRandomDistributionMode && !participationEndDate) {
      ctx.addIssue({
        message: 'ADD DATE',
        code: 'custom',
        path: ['participationEndDate'],
      });
    }
    if (!minNumberOfSeatsPerPosition) {
      ctx.addIssue({
        message: 'Invalid input',
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'null',
        path: ['minNumberOfSeatsPerPosition'],
      });
    }
    if (!maxNumberOfSeatsPerPosition) {
      ctx.addIssue({
        message: 'Invalid input',
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'null',
        path: ['maxNumberOfSeatsPerPosition'],
      });
    }

    if (
      minNumberOfSeatsPerPosition &&
      maxNumberOfSeatsPerPosition &&
      minNumberOfSeatsPerPosition > maxNumberOfSeatsPerPosition
    ) {
      ctx.addIssue({
        message: 'Invalid input',
        code: 'custom',
        path: ['maxNumberOfSeatsPerPosition'],
      });
    }
  })
  .superRefine((schema, ctx) => {
    const reminder = schema.rsvpReminderTimeOutInMs ?? 0;
    const expiration = schema.voucherExpirationTimeOutInMs ?? 0;

    if (reminder > 0 && reminder >= expiration) {
      ctx.addIssue({
        message: 'rsvp_reminder_before_expiration_validation',
        code: z.ZodIssueCode.custom,
        path: ['rsvpReminderTimeOutInMs'],
      });
    }
  })
  .superRefine((schema, ctx) => {
    const { positionsDistributionMode, displayMode } = schema;
    const randomDistributionIncompatibleDisplayModes = [
      DisplayMode.DETAILED,
      DisplayMode.DETAILED_LIMITED,
      DisplayMode.INFORMATIVE,
    ];
    const parkingDistributionIncompatibleDisplayModes = [
      DisplayMode.CONTEST,
      DisplayMode.MERCHANDISE,
      DisplayMode.INFORMATIVE,
    ];
    if (
      ([
        PositionsDistributionMode.RANDOM,
        PositionsDistributionMode.WEIGHTED_RANDOM,
      ].includes(positionsDistributionMode as PositionsDistributionMode) &&
        randomDistributionIncompatibleDisplayModes.includes(
          displayMode as DisplayMode
        )) ||
      (positionsDistributionMode === PositionsDistributionMode.WITH_PARKING &&
        parkingDistributionIncompatibleDisplayModes.includes(
          displayMode as DisplayMode
        ))
    ) {
      ctx.addIssue({
        message: 'Not allowed for selected allocation type',
        // positionsDistributionMode === PositionsDistributionMode.RANDOM
        //   ? 'Display mode DETAILED, INFORMATIVE not allowed for selected distribution mode'
        //   : 'Display mode  CONTEST, MERCHANDISE, INFORMATIVE not allowed for selected distribution mode',
        code: 'custom',
        path: ['displayMode'],
      });
    }
  })
  .superRefine((data, ctx) => {
    if (
      data.positionsDistributionMode === PositionsDistributionMode.WITH_PARKING
    ) {
      if (
        data.maxNumberOfParkingTicketsPerPosition === undefined ||
        data.maxNumberOfParkingTicketsPerPosition < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maxNumberOfParkingTicketsPerPosition'],
          message: 'exp_allocation_parking_max_required',
        });
      } else {
        // Fixed-seat lists carry maxNumberOfSeatsPerPosition = 0 and express the count via
        // fixedNumberOfSeatsPerPosition, so fall back to it for the ceiling (mirrors the
        // backend's effectiveSeatCeiling); otherwise every parking max >= 1 would be rejected.
        const effectiveSeatCeiling =
          data.maxNumberOfSeatsPerPosition &&
          data.maxNumberOfSeatsPerPosition > 0
            ? data.maxNumberOfSeatsPerPosition
            : data.fixedNumberOfSeatsPerPosition;
        if (
          effectiveSeatCeiling !== undefined &&
          data.maxNumberOfParkingTicketsPerPosition > effectiveSeatCeiling
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxNumberOfParkingTicketsPerPosition'],
            message: 'exp_allocation_parking_max_exceeds_seats',
          });
        }
      }
    }
  });

// export type ExperienceGeneralFormValidator = z.infer<
//   typeof CreateExperienceGeneralFormValidatorSchema
// >;
